import { NavLink } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { ChartCard } from "../components/ChartCard";
import { ChartTooltipContent } from "../components/ChartTooltipContent";
import { DataTableCard } from "../components/DataTableCard";
import { KpiRail } from "../components/KpiRail";
import { PremiumHero } from "../components/PremiumHero";
import { SignalPill } from "../components/SignalPill";
import { useContent } from "../hooks/useContent";
import { PageContainer } from "../layout/PageContainer";
import { api, type ContractedFieldGroupData, type ContractedPublicObjectData } from "../lib/api";

type FinancialsPageData = Awaited<ReturnType<typeof loadFinancialsPage>>;

async function loadFinancialsPage() {
  return api.getFinancials();
}

function formatCurrency(value: number) {
  return `$${value.toLocaleString()}`;
}

function formatBoolean(value: boolean) {
  return value ? "Yes" : "No";
}

function formatFieldGroupStatus(status: ContractedFieldGroupData["status"]) {
  switch (status) {
    case "current-public-data":
      return "Live";
    case "readable-promoted-source":
      return "Promoted";
    case "manifest-backed-pending-source-body":
      return "Pending body";
    default:
      return status;
  }
}

function buildCoverageNote(contractedObject: ContractedPublicObjectData) {
  const { readableCount, manifestOnlyCount, missingCount } = contractedObject.workingSnapshotCoverage;
  return `${readableCount} readable, ${manifestOnlyCount} manifest-backed, ${missingCount} missing`;
}

function buildContractRows(contractedObject: ContractedPublicObjectData) {
  return contractedObject.fieldGroups.map((group) => [group.name, group.currentRead, formatFieldGroupStatus(group.status)]);
}

function buildDisclosureRows(items: string[]) {
  return items.slice(0, 5).map((item, index) => [`Disclosure ${index + 1}`, item, "Live"]);
}

function FinancialsContent({ data }: { data: FinancialsPageData }) {
  const {
    economics,
    underwritingSummary,
    fallbackSummary,
    developmentBudget,
    costBreakdown,
    operatingModel,
    underwritingScenarios,
    sensitivityAnalysis,
    assumptionsRegister,
    fallbackEconomics
  } = data;
  const { costSummary, operationsSummary, keyFacts } = economics;

  const pressureData = [
    { name: "Cost", value: costSummary.openCostRisks.length },
    { name: "Ops", value: operationsSummary.openOperatingItems.length },
    { name: "UW", value: underwritingSummary.limits.length },
    { name: "Fallback", value: fallbackSummary.limits.length }
  ];

  const developmentBudgetRows = [
    ...buildContractRows(developmentBudget),
    ["Utility baseline", formatCurrency(costSummary.utilityBaseline.citySideKnownBaseline), "Verified"],
    ["Parking burden required", formatBoolean(costSummary.parkingCostBurdenRequired), "Live"]
  ];

  const underwritingRows = [
    ...buildContractRows(underwritingScenarios),
    ...underwritingSummary.corePosition.slice(0, 2).map((item) => [item.topic, item.currentRead, "Baseline"])
  ];

  const fallbackRows = [
    ...buildContractRows(fallbackEconomics),
    ...fallbackSummary.fallbackPaths.slice(0, 2).map((item) => [item.fallbackPath, item.currentBestRead, "Baseline"])
  ];

  const disclosureRows = [
    ...buildContractRows(assumptionsRegister),
    ...buildDisclosureRows([
      ...assumptionsRegister.disclosures,
      ...sensitivityAnalysis.disclosures,
      ...costBreakdown.disclosures,
      ...operatingModel.disclosures
    ])
  ];

  return (
    <div className="stack-xl">
      <PremiumHero
        eyebrow="Capital Screen"
        title="The deal is readable now. The remaining pressure is visible."
        subtitle="Financials now read as a compact investor screen: budget posture, scenario discipline, downside framing, and disclosures are visible without turning the page into a memo."
        backgroundImage="/comps/110-f-st-map.jpg"
        backgroundLabel="Grounded local context map from nearby comp set"
        badges={
          <>
            <SignalPill label={costSummary.costPosture} tone="warning" />
            <SignalPill label={underwritingSummary.status} tone="neutral" />
            <SignalPill label={fallbackSummary.status} tone="positive" />
          </>
        }
        actions={
          <>
            <NavLink to="/underwriting" className="link-button link-button-primary">
              Open Underwriting
            </NavLink>
            <NavLink to="/fallback" className="link-button link-button-secondary">
              Open Fallback
            </NavLink>
          </>
        }
        aside={
          <div className="stack-md">
            <div className="stack-xs">
              <span className="card-eyebrow">Top Line</span>
              <div style={{ fontSize: "1.9rem", lineHeight: 1, letterSpacing: "-0.04em", color: "#f6fbff" }}>
                {keyFacts.leadConcept}
              </div>
              <p style={{ margin: 0, color: "rgba(223,233,248,0.78)", lineHeight: 1.5 }}>
                Current financial review remains conditional, but the cost, ops, and fallback stack is now structured enough for capital screening.
              </p>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: "12px"
              }}
            >
              {[
                ["Utility anchor", formatCurrency(costSummary.utilityBaseline.citySideKnownBaseline)],
                ["Registration fee", formatCurrency(operationsSummary.registrationFee)],
                ["Penalty exposure", formatCurrency(operationsSummary.registrationPenalty)],
                ["Live pressure points", `${pressureData.reduce((sum, item) => sum + item.value, 0)}`]
              ].map(([label, value]) => (
                <div
                  key={label}
                  style={{
                    borderRadius: "18px",
                    padding: "14px",
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)"
                  }}
                >
                  <div style={{ fontSize: "0.72rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(198,211,234,0.62)" }}>
                    {label}
                  </div>
                  <div style={{ marginTop: "8px", color: "#f4f8ff", lineHeight: 1.35 }}>{value}</div>
                </div>
              ))}
            </div>
            <div
              style={{
                overflow: "hidden",
                borderRadius: "18px",
                border: "1px solid rgba(255,255,255,0.08)",
                background: "rgba(255,255,255,0.04)"
              }}
            >
              <img src="/comps/110-f-st-streetview.jpg" alt="Nearby built-product context" style={{ display: "block", width: "100%", height: "146px", objectFit: "cover" }} />
            </div>
          </div>
        }
      />

      <KpiRail
        items={[
          {
            label: "Budget posture",
            value: costSummary.costPosture,
            note: developmentBudget.fieldGroups[0]?.currentRead ?? "Live cost stack",
            tone: "warning"
          },
          {
            label: "Operating posture",
            value: operationsSummary.operatingPosture,
            note: operatingModel.fieldGroups[0]?.currentRead ?? "Operating model live",
            tone: "neutral"
          },
          {
            label: "Scenario posture",
            value: underwritingSummary.status,
            note: underwritingScenarios.fieldGroups[0]?.currentRead ?? underwritingSummary.currentBestRead,
            tone: "neutral"
          },
          {
            label: "Downside read",
            value: fallbackSummary.status,
            note: fallbackEconomics.fieldGroups[0]?.currentRead ?? fallbackSummary.currentBestRead,
            tone: "positive"
          }
        ]}
      />

      <div
        style={{
          display: "grid",
          gap: "18px",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))"
        }}
      >
        <ChartCard
          eyebrow="Pressure Map"
          title="Remaining pressure is concentrated in a few visible buckets"
          description="This is the live count of unresolved pressure points carried across the current public cost, operating, underwriting, and fallback layers."
        >
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={pressureData} margin={{ top: 12, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="rgba(255,255,255,0.08)" />
                <XAxis dataKey="name" tick={{ fill: "#d7e4f8", fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fill: "#9eb6d7", fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} content={<ChartTooltipContent title="Live pressure points" valueSuffix=" items" />} />
                <Bar dataKey="value" name="Open items" fill="#7fd0ff" radius={[10, 10, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <section
          style={{
            borderRadius: "24px",
            padding: "22px",
            background: "linear-gradient(180deg, rgba(9, 14, 26, 0.92), rgba(7, 11, 21, 0.82))",
            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow: "0 18px 42px rgba(3, 8, 20, 0.24)"
          }}
        >
          <div className="stack-md">
            <div className="stack-xs">
              <span className="card-eyebrow">Fixed Anchors</span>
              <h3 className="card-title">Known public financial anchors</h3>
            </div>
            <div style={{ display: "grid", gap: "12px" }}>
              {[
                ["City-side utility baseline", formatCurrency(costSummary.utilityBaseline.citySideKnownBaseline)],
                ["Registration fee", formatCurrency(operationsSummary.registrationFee)],
                ["Registration penalty", formatCurrency(operationsSummary.registrationPenalty)],
                ["Lodging tax", `${operationsSummary.lodgingTax.percent}% + ${formatCurrency(operationsSummary.lodgingTax.perRoomNight)} / room night`],
                ["Business personal property", `${formatBoolean(operationsSummary.businessPersonalProperty.applies)} | $${operationsSummary.businessPersonalProperty.ratePer100} per $100`]
              ].map(([label, value]) => (
                <div
                  key={label}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "16px",
                    paddingBottom: "12px",
                    borderBottom: "1px solid rgba(255,255,255,0.06)"
                  }}
                >
                  <span style={{ color: "rgba(216, 227, 244, 0.74)" }}>{label}</span>
                  <strong style={{ color: "#f4f8ff", textAlign: "right" }}>{value}</strong>
                </div>
              ))}
            </div>
            <div style={{ color: "rgba(216, 227, 244, 0.68)", fontSize: "0.92rem", lineHeight: 1.55 }}>
              Public-safe disclosed anchors stay visible here. Full pro forma precision still belongs to later data promotion, not invented runtime detail.
            </div>
          </div>
        </section>
      </div>

      <div
        style={{
          display: "grid",
          gap: "18px",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))"
        }}
      >
        <DataTableCard
          eyebrow="Development Budget"
          title="Development budget"
          description="Budget posture reads from the promoted contract first, then from the cleanest verified baseline anchors."
          columns={["Line", "Current read", "Status"]}
          rows={developmentBudgetRows}
          footer={<div style={{ color: "rgba(216, 227, 244, 0.68)", fontSize: "0.88rem" }}>Coverage: {buildCoverageNote(developmentBudget)}</div>}
        />

        <DataTableCard
          eyebrow="Underwriting Scenarios"
          title="Scenario table"
          description="Scenario logic is presented as a compact comparison layer, not a narrative underwriting memo."
          columns={["Scenario line", "Current read", "Status"]}
          rows={underwritingRows}
          footer={<div style={{ color: "rgba(216, 227, 244, 0.68)", fontSize: "0.88rem" }}>Current best read: {underwritingSummary.currentBestRead}</div>}
        />

        <DataTableCard
          eyebrow="Fallback Economics"
          title="Fallback economics"
          description="Fallback paths remain visible in table form so downside logic can be scanned instead of hunted through prose."
          columns={["Fallback line", "Current read", "Status"]}
          rows={fallbackRows}
          footer={<div style={{ color: "rgba(216, 227, 244, 0.68)", fontSize: "0.88rem" }}>Coverage: {buildCoverageNote(fallbackEconomics)}</div>}
        />

        <DataTableCard
          eyebrow="Assumptions & Disclosures"
          title="Disclosures"
          description="Assumptions and caveats are kept visible, but compressed into a read-once disclosure table."
          columns={["Item", "Current read", "Status"]}
          rows={disclosureRows}
          footer={<div style={{ color: "rgba(216, 227, 244, 0.68)", fontSize: "0.88rem" }}>Residual open items: {costSummary.openCostRisks.length + operationsSummary.openOperatingItems.length + underwritingSummary.limits.length}</div>}
        />
      </div>

      <section
        style={{
          borderRadius: "24px",
          padding: "22px",
          background: "linear-gradient(180deg, rgba(9, 14, 26, 0.92), rgba(7, 11, 21, 0.82))",
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "0 18px 42px rgba(3, 8, 20, 0.24)"
        }}
      >
        <div
          style={{
            display: "grid",
            gap: "18px",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
            alignItems: "center"
          }}
        >
          <div className="stack-xs">
            <span className="card-eyebrow">Supporting Detail</span>
            <h3 className="card-title">Open the deep financial routes after the screen-level read.</h3>
            <p className="card-copy">
              This page now carries the top-line capital read. Underwriting and fallback remain supporting detail, not the first screen.
            </p>
          </div>
          <div className="row-wrap gap-sm">
            <NavLink to="/underwriting" className="link-button link-button-primary">
              Underwriting Detail
            </NavLink>
            <NavLink to="/fallback" className="link-button link-button-secondary">
              Fallback Detail
            </NavLink>
          </div>
        </div>
      </section>
    </div>
  );
}

export function EconomicsPage() {
  const { data, loading, error } = useContent("financials-page", loadFinancialsPage);

  return (
    <PageContainer
      eyebrow="Financials"
      title="Capital screen. Not a memo."
      lede="Budget, scenario spread, downside posture, and disclosures are compressed into an investor-readable finance surface."
    >
      {loading ? <div className="loading-panel shimmer-card">Loading financials...</div> : null}
      {error ? <div className="error-panel">{error}</div> : null}
      {data ? <FinancialsContent data={data} /> : null}
    </PageContainer>
  );
}
